import React, { MouseEvent as ReactMouseEvent, RefObject, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import { useReactiveVar } from '@apollo/client';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { cx } from '@emotion/css';
import { useModal } from 'mui-modal-provider';

import { ToggleButtonGroup } from '../../../common/toggle-button-group';
import { OnlineShops } from '../online-shops';
import { useStyles } from './ecp-locator.styles';
import { EcpMap } from '../ecp-map';
import { EcpStores } from '../ecp-stores';
import { EcpSearch } from '../ecp-search';
import { EcpSort } from './ecp-sort/ecp-sort';
import { StoreDetail } from './store-detail';
import { BackToTopButton } from './back-to-top-button';
import { useStoreDetailFromCache } from '../../../../graphql/ecp-locator/actions/store-detail';
import { Page } from '../page/page';
import {
  ecpStoresCached,
  pinnedStoreCode,
  setAutocompletedSearch,
  setAutocompletedValue,
  setAutoValueSelected,
  setEcpFormSubmitted,
  setSearchBounds,
  setStoreCode,
  setStoreDetail,
} from '../../../../graphql/ecp-locator/ecp-locator.cache';
import { Icon, IconTypes } from '../../../common';
import { EcpFiltersModal } from '../ecp-filters-modal';
import { IEcpStore } from '../../../../rest/ecp-locator';
import { ReactComponent as ShowListIcon } from '../../../../assets/images/ecp/show-list.svg';
import { ReactComponent as ShowMapIcon } from '../../../../assets/images/ecp/show-map.svg';
import { BuySortTypes, EcpServices, IEcpLocatorProps } from './ecp-locator.models';
import { EcpTitle } from './ecp-title';
import { Preloader } from '../../../common/preloader';
import { ErrorPlain, ErrorTypes } from '../../../common/error-plain';
import { MobileViewTypes } from '../../../../graphql/ecp-locator/models';
import { Appointment } from '../user-activity-items';
import { setC2CMode } from '../../../../graphql/configuration/configuration.cache';
import { FilterButton } from './filter-button';
import { v2025PP18603 } from '../../../../constants/veeva';
import { Veeva } from '../../../common/veeva';
import { BackButton } from '../../../common/back-button-new';
import { useUpdateEcpStores } from '../../../../hooks';
import { NoFittings } from './no-fittings';
import { useSetStoreByAddress } from './hooks';
import { useBackToTop } from './hooks';
import { setNoLocationSearchReady } from '../../../../graphql/ecp-locator/ecp-locator.cache';

const toggleButtons = [
  {
    value: BuySortTypes.offline,
    children: <FormattedMessage id="ecp.offline" />,
    testId: 'ecp-sort-offline-button', // Don't remove data-testid, it's used by Ya Metrica
  },
  {
    value: BuySortTypes.online,
    children: <FormattedMessage id="ecp.online" />,
    testId: 'ecp-sort-online-button', // Don't remove data-testid, it's used by Ya Metrica
  },
];

export const EcpLocator = ({
  isFitted, isLoading, appointment, isLoggedIn, isResolved, hasExternalAddress, refetchProfile,
}: IEcpLocatorProps): JSX.Element => {
  const intl = useIntl();
  const classes = useStyles();
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const ecpService = (searchParams.get('ecpService') || EcpServices.fit) as EcpServices;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('xl'));
  const headerRef = useRef<HTMLDivElement | null>(null);
  const { showButton, scrollToTop } = useBackToTop(headerRef);
  const isTablet = !isMobile && !isDesktop;

  const [mobileView, setMobileView] = useState(MobileViewTypes.map);
  const [currentSortingType, setCurrentSortingType] = useState(BuySortTypes.offline);

  const handleToggle = (_event: ReactMouseEvent<HTMLElement>, newValue: BuySortTypes) => {
    setCurrentSortingType(newValue);
  };

  const { setStoreDetailToCache } = useStoreDetailFromCache();

  const ecpStores = useReactiveVar(ecpStoresCached);
  const stores = useSetStoreByAddress(ecpStores);
  const storeDetail = useReactiveVar(setStoreDetail);
  const storeCode = useReactiveVar(setStoreCode);
  const isEcpFormSubmitted = useReactiveVar(setEcpFormSubmitted);
  const c2CMode = useReactiveVar(setC2CMode);

  const { showModal } = useModal();

  const isStoreListShow = !storeDetail;
  const isLayerCard = isTablet || (isMobile && isStoreListShow);
  const isMapHidden = isMobile && (mobileView !== MobileViewTypes.map || !isStoreListShow);
  const isStoresShow = (!isMobile || mobileView === MobileViewTypes.list) && currentSortingType !== BuySortTypes.online;
  const isMobileButtonsShow = isMobile && isStoreListShow && currentSortingType === BuySortTypes.offline;
  const inputIcon = isMobile ? undefined : <Icon type={IconTypes.map} />;
  const resultsRef = useRef<HTMLUListElement | null>(null);
  const isErrorStores = isEcpFormSubmitted && !ecpStores.length && isStoresShow;

  const handleStoreSelect = (store: IEcpStore): void => {
    setStoreDetailToCache(store);
  };

  const handleStoreClose = (code: string) => {
    setStoreDetailToCache(null);
    // To manage scroll to store card.
    setStoreCode(code);
  };

  const handleScrollToStore = (
    currentStoreId: string,
    storeTitle: RefObject<HTMLHeadingElement>,
    storeId?: string,
  ) => {
    if (storeId?.length && storeId === currentStoreId) {
      storeTitle.current?.focus();
      storeTitle.current?.scrollIntoView();
      setStoreCode('');
    }
  };

  const { updateStores } = useUpdateEcpStores();

  const handleFilterButtonClick = () => {
    showModal(EcpFiltersModal, { ecpService, updateStores });
  };

  const isAppointmentShow = !!appointment && currentSortingType === BuySortTypes.offline;
  const isNoFittingsShow = isLoggedIn && ecpService === EcpServices.buy
    && currentSortingType === BuySortTypes.offline && !isFitted;
  const isMapVisible = mobileView !== MobileViewTypes.list
    && !(currentSortingType === BuySortTypes.online && !isDesktop);

  useEffect(() => {
    setAutocompletedValue('');
    setSearchBounds(null);

    return () => {
      setStoreDetailToCache(null);
      setStoreCode('');
      setStoreDetail(null);
      setAutocompletedValue('');
      setEcpFormSubmitted(false);
      setAutoValueSelected(false);
      setAutocompletedSearch(false);
      setSearchBounds(null);
      pinnedStoreCode('');
    };
  }, []);

  useEffect(() => {
    if (!hasExternalAddress || isResolved) {
      setNoLocationSearchReady(true);
    }
  }, []);

  return (
    <Page
      title={intl.formatMessage({ id: 'ecpLocatorPage.title' })}
      description={intl.formatMessage({ id: 'ecpLocatorPage.description' })}
    >
      <div
        className={cx(classes.root, {
          [classes.fullHeight]: mobileView === MobileViewTypes.list && isMobile && isStoresShow,
        })}
        data-testid="ecp-locator"
      >
        <div
          className={cx(classes.ecpLayerRoot, {
            [classes.ecpLayerRootCard]: isLayerCard,
          })}
        >
          {isStoreListShow && (
            <>
              <div
                className={classes.ecpLayerWrapper}
                data-testid="ecp-stores-container"
              >
                <div ref={headerRef}>
                  <div className={classes.titleBar}>
                    <BackButton isHideTextMobile />
                    <EcpTitle ecpService={ecpService} />
                  </div>
                  {ecpService === EcpServices.buy && (
                    <ToggleButtonGroup
                      fullWidth
                      exclusiveExactly
                      withoutMargins
                      size="small"
                      variant="outlined"
                      options={toggleButtons}
                      value={currentSortingType}
                      onChange={handleToggle}
                      exclusive
                      groupStyles={classes.toggle}
                    />
                  )}
                  {(ecpService === EcpServices.buy && currentSortingType === BuySortTypes.online) && (
                    <OnlineShops />
                  )}
                  {currentSortingType === BuySortTypes.offline && (
                    <>
                      <div className={classes.searchBar}>
                        <EcpSearch
                          inputIcon={inputIcon}
                          inputStyles={classes.input}
                          rootInputStyles={classes.inputRoot}
                          isC2c={false}
                          updateStores={updateStores}
                        />
                        {ecpService !== EcpServices.cataract && (
                          <div className={classes.filterButtonDesktop}>
                            <FilterButton onClick={handleFilterButtonClick} />
                          </div>
                        )}
                      </div>
                      {isStoresShow && (
                        <div className={classes.sortButtons}>
                          <EcpSort />
                        </div>
                      )}
                    </>
                  )}
                </div>
                {isErrorStores && (
                  <ErrorPlain
                    content="ecp.error.noResults"
                    isolated
                    variant={ErrorTypes.plainText}
                  />
                )}
                {isStoresShow && (
                  <div className={cx(classes.storesList)}>
                    <>
                      {isLoading && <Preloader />}
                      {ecpStores?.length ? <EcpStores
                        ref={resultsRef}
                        stores={stores}
                        onStoreSelect={handleStoreSelect}
                        storeCode={storeCode}
                        onScrollToStore={handleScrollToStore}
                      /> : <></>}
                    </>
                    <BackToTopButton visible={showButton} onClick={scrollToTop}/>
                  </div>
                )}
              </div>
            </>
          )}
          {storeDetail && (
            <div className={classes.ecpLayerWrapper}>
              <StoreDetail
                storeData={storeDetail as IEcpStore}
                onStoreClose={handleStoreClose}
                isFitted={isFitted!}
                c2CMode={c2CMode}
                ecpService={ecpService}
                isNoFittingsShow={!!isNoFittingsShow}
                hasAppointment={isAppointmentShow}
                refetchProfile={refetchProfile}
              />
            </div>
          )}
        </div>
        {isMapVisible && (
          <div
            data-testid="ecp-map-container"
            className={cx(classes.mapLayer,
              { [classes.mapLayerHidden]: isMapHidden },
              { [classes.mapLayerLong]: ecpService === EcpServices.buy },
              { [classes.mapInactive]: currentSortingType === BuySortTypes.online },
            )}
          >
            <EcpMap
              ecpStores={ecpStores}
              storeDetail={storeDetail as IEcpStore}
              isMapHidden={isMapHidden}
              isMarkersHidden={currentSortingType === BuySortTypes.online}
              isControlsHidden={currentSortingType === BuySortTypes.online}
            />
            {(isAppointmentShow || isNoFittingsShow) && (
              <div
                data-testid="appointment-wrapper"
                className={classes.appointmentWrapper}
              >
                {isAppointmentShow ? <Appointment /> : !storeDetail ? <NoFittings /> : null}
              </div>
            )}
          </div>
        )}
        {isMobileButtonsShow && (
          <div className={cx(classes.mobileButtonsBar,
            { [classes.listButtons]: mobileView === MobileViewTypes.list && isErrorStores })}>
            {ecpService !== EcpServices.cataract && (
              <div className={classes.mobileButtonsCenter}>
                <FilterButton onClick={handleFilterButtonClick} />
              </div>
            )}
            <div className={classes.mobileViewButton}>
              <button
                className={classes.mobileViewButtonStyle}
                onClick={() => setMobileView(
                  mobileView === MobileViewTypes.map
                    ? MobileViewTypes.list
                    : MobileViewTypes.map,
                )}
              >
                {mobileView === MobileViewTypes.map ? (
                  <ShowListIcon width="24" height="24" />
                ) : (
                  <ShowMapIcon width="24" height="24" />
                )}
              </button>
            </div>
          </div>
        )}
        {(mobileView === MobileViewTypes.list) && (
          <Veeva veevaNumber={v2025PP18603} isLeft />
        )}
      </div>
    </Page>
  );
};
